<template>
  <div class="role-create-page">
    <div class="page-header mb-5">
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <h1 class="title is-3">Create New Role</h1>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <router-link to="/roles" class="button is-light">
              <span class="icon">
                <i class="bx bx-arrow-back"></i>
              </span>
              <span>Back to Roles</span>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <div class="columns">
      <div class="column is-8 is-offset-2">
        <div class="card">
          <header class="card-header has-background-primary">
            <p class="card-header-title has-text-white">Role Information</p>
          </header>
          <div class="card-content">
            <form @submit.prevent="saveRole">
              <div class="field">
                <label class="label">Role Name</label>
                <div class="control">
                  <input 
                    v-model="form.name" 
                    class="input" 
                    type="text" 
                    placeholder="Enter role name" 
                    required
                  >
                </div>
                <p v-if="errors.name" class="help is-danger">{{ errors.name }}</p>
              </div>

              <div class="field">
                <label class="label">Description</label>
                <div class="control">
                  <textarea 
                    v-model="form.description" 
                    class="textarea" 
                    placeholder="Enter role description"
                  ></textarea>
                </div>
                <p v-if="errors.description" class="help is-danger">{{ errors.description }}</p>
              </div>

              <div class="field">
                <label class="label">Permissions</label>
                <div v-if="loading" class="has-text-centered py-3">
                  <span class="icon is-medium has-text-primary">
                    <i class="bx bx-loader bx-spin"></i>
                  </span>
                  <p class="has-text-grey">Loading permissions...</p>
                </div>
                <div v-else-if="permissions.length === 0" class="notification is-light is-warning">
                  <p>No permissions available. Please create permissions first.</p>
                </div>
                <div v-else>
                  <div class="permission-groups">
                    <div v-for="(groupPermissions, group) in groupedPermissions" :key="group" class="permission-group mb-5">
                      <h4 class="subtitle is-5 mb-3">
                        <span class="tag is-primary is-medium mr-2">{{ formatGroupName(group) }}</span>
                        <button 
                          type="button" 
                          class="button is-small" 
                          @click="toggleGroupSelection(group, groupPermissions)"
                        >
                          {{ allGroupSelected(group) ? 'Deselect All' : 'Select All' }}
                        </button>
                      </h4>
                      <div class="columns is-multiline">
                        <div 
                          v-for="permission in groupPermissions" 
                          :key="permission.id"
                          class="column is-4"
                        >
                          <div class="box p-3 has-background-light">
                            <label class="checkbox">
                              <input 
                                type="checkbox"
                                :value="permission.id"
                                v-model="form.permissions"
                              >
                              {{ permission.name }}
                            </label>
                            <p class="help has-text-grey">{{ permission.description }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="field mt-5">
                <div class="control">
                  <button 
                    type="submit" 
                    class="button is-primary is-fullwidth" 
                    :class="{'is-loading': saving}"
                  >
                    Create Role
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import { useRouter } from 'vue-router'

export default {
  name: 'RoleCreate',
  setup() {
    const router = useRouter()
    const permissions = ref([])
    const loading = ref(true)
    const saving = ref(false)
    const errors = ref({})

    const form = ref({
      name: '',
      description: '',
      permissions: []
    })

    const fetchPermissions = async () => {
      loading.value = true
      try {
        const response = await axios.get('/api/permissions')
        permissions.value = response.data.data || []
      } catch (error) {
        console.error('Error fetching permissions:', error)
      } finally {
        loading.value = false
      }
    }

    const groupedPermissions = computed(() => {
      const grouped = {}

      permissions.value.forEach(permission => {
        const group = permission.group || 'general'
        if (!grouped[group]) {
          grouped[group] = []
        }
        grouped[group].push(permission)
      })

      return grouped
    })

    const formatGroupName = (group) => {
      return group.split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    }

    const allGroupSelected = (group) => {
      const groupPermissionIds = groupedPermissions.value[group].map(p => p.id)
      return groupPermissionIds.every(id => form.value.permissions.includes(id))
    }

    const toggleGroupSelection = (group, groupPermissions) => {
      const groupPermissionIds = groupPermissions.map(p => p.id)

      if (allGroupSelected(group)) {
        // Remove all from this group
        form.value.permissions = form.value.permissions.filter(id => !groupPermissionIds.includes(id))
      } else {
        // Add all from this group
        const newPermissions = [...form.value.permissions]
        groupPermissionIds.forEach(id => {
          if (!newPermissions.includes(id)) {
            newPermissions.push(id)
          }
        })
        form.value.permissions = newPermissions
      }
    }

    const saveRole = async () => {
      saving.value = true
      errors.value = {}

      try {
         await axios.post('/api/roles', form.value)
        router.push({
          path: '/roles',
          query: { success: `Role '${form.value.name}' created successfully` }
        })
      } catch (error) {
        console.error('Error creating role:', error)
        if (error.response && error.response.data.errors) {
          errors.value = error.response.data.errors
        } else {
          alert('Failed to create role. Please try again.')
        }
      } finally {
        saving.value = false
      }
    }

    onMounted(fetchPermissions)

    return {
      form,
      permissions,
      loading,
      saving,
      errors,
      groupedPermissions,
      formatGroupName,
      allGroupSelected,
      toggleGroupSelection,
      saveRole
    }
  }
}
</script>

<style scoped>
.role-create-page {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.permission-group {
  padding-bottom: 1rem;
  border-bottom: 1px solid #f0f0f0;
}

.permission-group:last-child {
  border-bottom: none;
}
</style>
